import React from 'react';
import { Heart, Award, Users, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { mockSocialPosts } from '../data/mockData';

const About = () => {
  const accomplishments = [
    {
      icon: <Sprout className="w-8 h-8" />,
      title: 'Adopt-a-Mile',
      description: 'Regular community cleanup initiatives keeping our neighborhoods clean and beautiful'
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'STEM Day',
      description: 'Engaging youth in hands-on science, technology, engineering, and mathematics activities'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Community Service Events',
      description: 'Partnerships with organizations like St. Jude, Goodwill, and Hosea Feed the Hungry'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'PTA & School Activities',
      description: 'Supporting Birmingham Public Schools, Marietta City Schools, and educational initiatives'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Financial Literacy',
      description: 'Providing essential financial education and money management skills to communities'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Youth Mentoring',
      description: 'First Look Youth Council, Boys and Girls Club, and Mass Mentoring Partnership programs'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About EPPS Helps</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-3">
              Preserving a legacy of service and empowering communities for generations to come
            </p>
            <p className="text-lg text-blue-200">
              Emmett Pollard Philanthropy Society
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Story</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-yellow-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="bg-gray-50 rounded-2xl p-4">
              <img
                src="https://customer-assets.emergentagent.com/job_1715fc91-82d5-4329-a769-57f0c42f446b/artifacts/nqusl0d8_1000036505.jpg"
                alt="EPPS Helps team and family"
                className="rounded-2xl shadow-2xl w-full h-auto object-contain"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Preserving a Legacy</h3>
              <p className="text-gray-700 leading-relaxed text-lg mb-4">
                EPPS Helps was started to preserve the legacy of <strong>Emmett George Pollard, Sr</strong>, affectionately known as "George". Those who knew him can remember his greater-than-life personality and the joy he brought into the lives of every person he encountered.
              </p>
              <p className="text-gray-700 leading-relaxed text-lg">
                Currently run by his son, <strong>Emmett G. Pollard, Jr.</strong>, EPPS Helps focuses on initiatives that develop and focus on the well-being of the community.
              </p>
            </div>
          </div>

          <Card className="border-none shadow-xl bg-gradient-to-r from-blue-50 to-gray-50">
            <CardContent className="p-8 md:p-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">The Journey of Emmett G. Pollard, Jr.</h3>
              
              <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
                <p>
                  Pollard, Jr. was born in <strong>Birmingham, Alabama</strong>, and was a product of Jackson-Olin High School, home to the likes of Eddie Kendricks of The Temptations and Arnetta Nunn, the first female Police Chief of the City of Birmingham. At Jackson-Olin, Pollard found his niche for philanthropy.
                </p>
                
                <p>
                  Some of his work during this time included assisting in the organization of the <strong>Fox 6 School Cleanup</strong> and providing volunteer services for the <strong>First Look Youth Council</strong>, including grant writing and urban revitalization projects.
                </p>
                
                <p>
                  Upon matriculating at the <strong>University of Alabama</strong> in 2000, Pollard continued to strive in this area, where he participated in the Capstone Event, various Habitat for Humanity projects, and promoted voter registration.
                </p>
                
                <p>
                  After relocating to <strong>Jackson, Mississippi</strong>, to attend graduate school at Jackson State University, he immediately began working in his niche again by focusing on childhood development at the <strong>Boys and Girls Club of Jackson</strong> and providing mathematics enrichment to undergraduate students at Jackson State University.
                </p>
                
                <p>
                  After years of serving the community, Pollard has decided to start a foundation in order to continue his efforts in cities he has yet to reach, as well as maximize his philanthropy works for each community which has helped mold him into the person he is today.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Accomplishments Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Key accomplishments and initiatives that define our commitment to community service
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-yellow-600 mx-auto mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {accomplishments.map((item, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 text-white">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-12 border-none shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Organizations We've Supported</h3>
              <div className="grid md:grid-cols-2 gap-4 text-gray-700">
                <ul className="space-y-2">
                  <li>• St. Jude Children's Research Hospital</li>
                  <li>• ABC's Extreme Home Makeover</li>
                  <li>• Congressional Black Caucus Foundation</li>
                  <li>• The Coca-Cola Scholars Foundation</li>
                  <li>• Goodwill Industries (Southern Arizona & Atlanta)</li>
                </ul>
                <ul className="space-y-2">
                  <li>• Birmingham Public Schools</li>
                  <li>• Mass Mentoring Partnership</li>
                  <li>• Team ATL: Super Bowl 53</li>
                  <li>• Marietta City Schools</li>
                  <li>• Hosea Feed the Hungry</li>
                  <li>• Keep Marietta Beautiful</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Follow Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay connected with us on social media to see our latest community initiatives
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-yellow-600 mx-auto mt-6"></div>
            <div className="mt-6 text-gray-600">
              <p className="text-sm">Note: Social media feeds below are sample posts. Live feed integration requires backend setup.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockSocialPosts.map((post) => (
              <Card key={post.id} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <div className="relative h-64">
                  <img
                    src={post.image}
                    alt={post.caption}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-blue-600">
                    {post.platform}
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-800 leading-relaxed mb-3">{post.caption}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{post.date}</span>
                    <span className="flex items-center">
                      <Heart className="w-4 h-4 mr-1 text-red-500" />
                      {post.likes}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">Follow us for more updates!</p>
            <div className="flex justify-center space-x-4">
              <a
                href="https://instagram.com/eppshelps"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg font-semibold hover:from-pink-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300"
              >
                @eppshelps on Instagram
              </a>
              <a
                href="https://twitter.com/eppshelps"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-gradient-to-r from-blue-400 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-500 hover:to-blue-700 transform hover:scale-105 transition-all duration-300"
              >
                @eppshelps on Twitter
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Add missing imports at the top
import { Sprout, GraduationCap, TrendingUp } from 'lucide-react';

export default About;
